import { useState, useCallback, useEffect, useMemo } from 'react';
import keycloak from '../services/keycloak';

const CONFIGURED_SRE_URL = import.meta.env.VITE_SRE_API_URL || '';

export const useAskAi = () => {
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      sender: 'agent',
      agentType: 'Level 1: Information Agent',
      text: 'Hello! I am your 24/7 **Yaseero SRE Assistant**.\n\nYou can ask me about **warehouse workflows**, **system architecture**, **database migration rules**, or click **Diagnose** to inspect active system errors.',
      sources: [
        { document: '01-yaseero-domain-and-business-capabilities.md', section: 'Warehouse Operations' },
        { document: '02-yaseero-coding-patterns-and-architecture.md', section: 'Layered Flow' }
      ],
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [healthStatus, setHealthStatus] = useState(null);
  const [isHealthLoading, setIsHealthLoading] = useState(false);
  const [telemetryData, setTelemetryData] = useState(null);
  const [isTelemetryLoading, setIsTelemetryLoading] = useState(false);

  // Super Admin Role Inspection
  const userRoles = useMemo(() => {
    return keycloak.tokenParsed?.realm_access?.roles || [];
  }, [keycloak.tokenParsed]);

  const isSuperAdmin = useMemo(() => {
    const adminRoles = ['TENANT_ADMIN', 'SUPER_ADMIN', 'ADMIN'];
    return userRoles.some(role => adminRoles.includes(role));
  }, [userRoles]);

  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json'
    };
    if (keycloak && keycloak.token) {
      headers['Authorization'] = `Bearer ${keycloak.token}`;
    }
    return headers;
  };

  /**
   * Helper that attempts primary configured URL, and falls back to local Vite proxy
   * if the remote demo server is not yet hosting the SRE container.
   */
  const executeApiCall = async (path, options = {}) => {
    const candidateUrls = [];
    if (CONFIGURED_SRE_URL) {
      candidateUrls.push(`${CONFIGURED_SRE_URL}${path}`);
    }
    // Always include local origin fallback via Vite proxy /api/sre
    const localProxyUrl = `${window.location.origin}${path}`;
    if (!candidateUrls.includes(localProxyUrl)) {
      candidateUrls.push(localProxyUrl);
    }
    // Also include direct local backend port 8090 if running standalone
    const directLocalUrl = `http://localhost:8090${path}`;
    if (!candidateUrls.includes(directLocalUrl)) {
      candidateUrls.push(directLocalUrl);
    }

    let lastError = null;

    for (const url of candidateUrls) {
      try {
        const response = await fetch(url, {
          ...options,
          headers: {
            ...getAuthHeaders(),
            ...(options.headers || {})
          }
        });

        const contentType = response.headers.get('content-type') || '';

        // If server returned HTML (Vite index.html fallback), it's not the SRE API!
        if (contentType.includes('text/html')) {
          throw new Error(`Endpoint returned HTML SPA instead of JSON API (service not mounted at ${url})`);
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        return { data, activeEndpoint: url };
      } catch (err) {
        lastError = err;
      }
    }

    throw lastError || new Error('Failed to reach SRE Agent across configured endpoints');
  };

  const fetchHealth = useCallback(async () => {
    setIsHealthLoading(true);
    try {
      const { data } = await executeApiCall('/api/sre/health');
      setHealthStatus(data);
    } catch (err) {
      setHealthStatus({
        status: 'UNREACHABLE',
        error: err.message,
        hint: CONFIGURED_SRE_URL.includes('demo')
          ? 'Demo server container not yet started or Nginx /api/sre/ proxy not active.'
          : 'Local SRE container/process on port 8090 is not running.'
      });
    } finally {
      setIsHealthLoading(false);
    }
  }, []);

  const fetchTelemetry = useCallback(async () => {
    if (!isSuperAdmin) return;
    setIsTelemetryLoading(true);
    try {
      const { data } = await executeApiCall('/api/sre/telemetry');
      setTelemetryData(data);
    } catch (err) {
      console.warn('[AskAI] Failed to fetch live telemetry:', err);
    } finally {
      setIsTelemetryLoading(false);
    }
  }, [isSuperAdmin]);

  const sendMessage = useCallback(async (promptText, options = {}) => {
    if (!promptText || !promptText.trim()) return;

    const userMsg = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: promptText,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const payload = {
        prompt: promptText,
        source: 'yaseero-web',
        stackTrace: options.stackTrace || null,
        apiEndpoint: options.apiEndpoint || null,
        statusCode: options.statusCode || null,
        userRole: userRoles,
        pageContext: window.location.pathname
      };

      const { data } = await executeApiCall('/api/sre/orchestrator/ask', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      let responseText = '';
      let sources = [];
      let diagnosticSteps = [];
      let remediation = null;

      if (data.routed_to?.includes('Information Agent')) {
        responseText = data.answer || 'Information retrieved from knowledge base.';
        sources = data.sources || [];
      } else {
        // Level 2 Reasoning Agent response
        responseText = `### 🔍 Incident Diagnosis: ${data.matched_runbook || 'Dynamic Investigation'}\n\n` +
          `**Root Cause:**\n${data.root_cause || data.root_cause_hypothesis || 'Under investigation.'}\n\n` +
          (data.remediation_plan ? `**Recommended Action:**\n${data.remediation_plan}` : '');
        remediation = data.remediation_plan || null;
        diagnosticSteps = data.diagnostic_steps || [];
      }

      const agentMsg = {
        id: `agent-${Date.now()}`,
        sender: 'agent',
        agentType: data.routed_to || 'SRE Agent',
        routingReason: data.routing_reason,
        text: responseText,
        sources: sources,
        diagnosticSteps: diagnosticSteps,
        remediation: remediation,
        requiresApproval: data.requires_hitl_approval,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      setMessages(prev => [...prev, agentMsg]);
    } catch (err) {
      const isHtmlError = err.message?.includes('HTML SPA');
      let guidance = '';
      if (isHtmlError) {
        guidance = `\n\n*The Demo Server (${CONFIGURED_SRE_URL || 'demo.yaseero.ae'}) returned the web frontend instead of the SRE orchestrator API.*`;
      }

      const errorMsg = {
        id: `err-${Date.now()}`,
        sender: 'agent',
        agentType: 'Error',
        text: `⚠️ **Unable to reach SRE Agent**: ${err.message}${guidance}\n\n**Quick resolution:**\n1. If deploying to demo server, start the container: \`sudo docker compose -f app-stack.yml up -d sre-orchestrator\`\n2. Or run locally: \`python -m uvicorn sre_agent.orchestrator.api:app --port 8090\``,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  }, [userRoles]);

  const clearMessages = useCallback(() => {
    setMessages([
      {
        id: 'welcome',
        sender: 'agent',
        agentType: 'Level 1: Information Agent',
        text: 'Chat history cleared. How can I assist you with Yaseero operations today?',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
    ]);
  }, []);

  return {
    messages,
    isLoading,
    sendMessage,
    clearMessages,
    healthStatus,
    fetchHealth,
    isHealthLoading,
    telemetryData,
    fetchTelemetry,
    isTelemetryLoading,
    isSuperAdmin,
    userRoles
  };
};
